import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, Validators } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { Authentication } from '../../services/authentication.service';
import { ErrorHandlingService } from '../../services/error-handling.service';
import { NotificationService } from '../../services/notification.service';
import { AccountService } from '../../services/account.service';
import { TitleService } from '../../services/title.service';

@Component({
  standalone: false,
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
})
export class SignUpComponent implements OnInit {
  userName: string;
  password = '';
  organization = '';
  isAdd = false;
  @ViewChild('resultModal') resultModal: any;
  closeResult = '';
  emailErrorMessage = '';
  passwordErrorMessage = '';
  submitMessage = '';
  email_error = false;
  email_verified_error = false;
  password_error = false;
  termsAndPrivacy = false;

  emailFC = new FormControl('', [Validators.required, Validators.email]);
  passwordFC = new FormControl('', [Validators.required]);
  organizationFC = new FormControl('', []);
  termsAndPrivacyFC = new FormControl(false, [Validators.required]);

  constructor(
    private authService: Authentication,
    private accountService: AccountService,
    private router: Router,
    private ngxLoader: NgxUiLoaderService,
    private modalService: NgbModal,
    private errorHandlingService: ErrorHandlingService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private titleService: TitleService,
  ) {
    if (this.authService.isTokenValid()) {
      this.router.navigate(['dashboard']);
    }
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.activatedRoute.snapshot, this.router.routerState.snapshot);
    this.termsAndPrivacyFC.valueChanges.pipe(distinctUntilChanged()).subscribe((res) => {
      if (this.termsAndPrivacyFC.value[0]) {
        this.termsAndPrivacy = true;
      } else {
        this.termsAndPrivacy = false;
      }
    });
  }

  onSubmit() {
    this.isAdd = true;
    this.userName = this.emailFC.value;
    this.password = this.passwordFC.value;
    this.organization = this.organizationFC.value;

    this.validateEmail();
    this.validatePassword();
    if (!this.termsAndPrivacy || this.email_error || this.password_error) {
      this.errorHandlingService.showError('Please fill the missing fields');
      return;
    }
    this.ngxLoader.start();
    this.accountService.signUp(this.userName.toLowerCase(), this.password, this.organization === '' ? null : this.organization).subscribe(
      (data) => {
        this.ngxLoader.stop();
        this.notificationService.showSuccess('Signed up successfully');
        this.router.navigate(['/email-verify'], {
          queryParams: { username: data.username },
        });
      },
      (err) => {
        this.ngxLoader.stop();
        console.log(err);
        this.errorHandlingService.showError(err?.error?.message);
        this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  validateEmail() {
    this.accountService.checkUserDuplicate(this.userName).subscribe((data) => {
      if ('username' in data && 'verified' in data && this.userName === data.username && data.verified) {
        this.errorHandlingService.showError('Username is taken');
        this.emailErrorMessage = 'This username already exists.';
        this.email_error = true;
        this.email_verified_error = false;
      } else if ('username' in data && 'verified' in data && this.userName === data.username && !data.verified) {
        this.errorHandlingService.showError('Username is taken and not verified');
        this.emailErrorMessage = 'This username already exists but it is not verified.';
        this.email_error = false;
        this.email_verified_error = true;
      } else {
        const email = this.userName;
        const re = /\S+@\S+\.\S+/;
        const valid = re.test(email);
        if (email.length > 0 && (valid == null || valid === false)) {
          this.errorHandlingService.showError('Username is invalid');
          this.emailErrorMessage = 'Username is invalid';
          this.email_error = false;
          this.email_verified_error = false;
        } else {
          this.emailErrorMessage = '';
          this.email_error = false;
          this.email_verified_error = false;
        }
      }
    });
  }

  resendVerificationEmail(): any {
    this.ngxLoader.start();
    this.userName = this.emailFC.value;
    this.accountService.resendVerificationEmail(this.userName.toLowerCase()).subscribe(
      (data) => {
        this.ngxLoader.stop();
        this.notificationService.showSuccess(`Verification email resent to ${this.userName}`);
      },
      (err) => {
        this.ngxLoader.stop();
        return this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  validatePassword() {
    this.password = this.passwordFC.value;
    const password = this.password;
    const valid = password.length >= 8;
    if (password.length > 0 && (valid == null || valid === false)) {
      this.errorHandlingService.showError('Password is too short.  Must be at least 8 characters.');
      this.passwordErrorMessage = 'Password is too short.  Must be at least 8 characters';
      this.password_error = true;
    } else {
      this.passwordErrorMessage = '';
      this.password_error = false;
    }
  }

  resetPasswordValidation() {
    this.password_error = false;
  }

  onStrengthChanged(strength: number) {
    console.log('password strength = ', strength);
  }
  openModal() {
    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationStart) {
        this.modalService.dismissAll();
      }
    });
    this.modalService.open(this.resultModal, { ariaLabelledBy: 'modal-title' }).result.then(
      (result) => {
        this.closeResult = `Closed with: ${result}`;
      },
      (reason) => {
        this.closeResult = `Dismissed `;
      },
    );
  }

  navigate() {
    window.location.href = 'https://www.terminologyhub.com';
  }

  googleLogin() {
    this.authService.loginWithGoogle().subscribe(
      (data: any) => {
        if (data && data.body) {
          location.href = data.body;
        }
      },
      (error) => {
        this.ngxLoader.stop();
        this.errorHandlingService.handleError(error, error.url);
      },
    );
  }

  microsoftLogin() {
    this.authService.loginWithMicrosoft().subscribe(
      (data: any) => {
        if (data && data.body) {
          location.href = data.body;
        }
      },
      (error) => {
        this.ngxLoader.stop();
        this.errorHandlingService.handleError(error, error.url);
      },
    );
  }
}
